import { BaseIdNotRequiredRequest } from "../../../base/request/BaseIdNotRequiredRequest";
import { toISODate } from "../../../utils/fechaUtils";
import {
  AplicacionResponse,
  MetodoAplicacion,
} from "../response/AplicacionResponse";
import {
  AplicacionDetalleRequest,
  fx_createAplicacionDetalle,
} from "./AplicacionDetalleRequest";
import {
  AplicacionLoteRequest,
  fx_createAplicacionLoteRequest,
} from "./AplicacionLoteRequest";

/** Request de guardado (crear/editar) de una aplicación. `id` null = crear;
 *  con valor = editar (mismo endpoint `POST /save`). */
export type AplicacionRequest = BaseIdNotRequiredRequest & {
  /** Finca de la aplicación. */
  fincaId: string;
  /** Cultivo asociado. */
  cultivoId: string;
  /** Tratamiento de cultivo asociado. */
  cultivoTratamientoId: string;
  /** Fecha de la aplicación (ISO date). */
  fechaAplicacion: string;
  /** URL del archivo de vuelo subido (null si no hay). Solo dron; el server la
   *  exige al **crear** una aplicación por dron. */
  vueloUrl: string | null;
  /** Productos aplicados con su dosis (reemplaza la lista completa). */
  detalles: AplicacionDetalleRequest[];
  /** Con qué se aplicó. Decide qué campos de abajo exige el server. */
  metodo: MetodoAplicacion;
  /** Recipiente con el que se mide (requerido en los métodos manuales). */
  recipienteId: string | null;
  /** Mililitros de mezcla por planta (requerido en drench). */
  mezclaPorPlantaMl: number | null;
  /** Capacidad de la bomba de espalda, en litros (opcional). */
  capacidadBombaLitros: number | null;
  /** Equipo con el que se aplicó (opcional, texto libre). */
  equipo: string | null;
  /** Quién aplicó (requerido en los métodos manuales). */
  responsable: string | null;
  /** Lotes aplicados (reemplaza la lista completa). El server exige al menos
   *  uno en los métodos manuales y ninguno se puede repetir. */
  lotes: AplicacionLoteRequest[];
};

export const fx_createAplicacionRequestEmpty = (
  fincaId: string,
): AplicacionRequest => {
  return {
    cultivoId: "",
    cultivoTratamientoId: "",
    detalles: [],
    fechaAplicacion: toISODate(new Date()),
    fincaId: fincaId,
    id: null,
    vueloUrl: null,
    // Dron es el método de entrada: es el que el módulo registró siempre y el
    // único que no pide nada más que el ZIP.
    metodo: "dron",
    recipienteId: null,
    mezclaPorPlantaMl: null,
    capacidadBombaLitros: null,
    equipo: null,
    responsable: null,
    lotes: [],
  };
};

export const fx_createAplicacionRequest = (
  response: AplicacionResponse,
): AplicacionRequest => {
  return {
    cultivoId: response.cultivoId ?? "",
    cultivoTratamientoId: response.cultivoTratamientoId ?? "",
    detalles: response.detalles.map((detalle) => {
      return fx_createAplicacionDetalle(detalle);
    }),
    fechaAplicacion: response.fechaAplicacion,
    fincaId: response.fincaId,
    id: response.id,
    // El ZIP no se re-sube al editar (null = conservar el vuelo actual).
    vueloUrl: null,
    metodo: response.metodo,
    recipienteId: response.recipienteId,
    mezclaPorPlantaMl: response.mezclaPorPlantaMl,
    capacidadBombaLitros: response.capacidadBombaLitros,
    equipo: response.equipo,
    responsable: response.responsable,
    lotes: response.lotes.map((lote) => fx_createAplicacionLoteRequest(lote)),
  };
};
